<template>
  <TransitionGroup name="appear">
    <!-- Host not found -->
    <NotFound
      v-if="hostNotFound"
      data-test="not-found"
    />

    <!-- Loader-->
    <div
      v-else-if="!donorStore.hosts_id || !initialized"
      class="flex grow absolute inset-x-0 inset-y-0"
      data-test="loader"
    >
      <div class="flex grow items-center justify-center w-full">
        <div class="flex flex-row items-center justify-center h-full">
          <div class="loader"></div>
        </div>
      </div>
    </div>

    <!-- Main container -->
    <StepsTakeover
      v-else
      :slideOut="slideOut"
      :slideIn="slideIn"
      data-test="steps-takeover"
    >
      <!-- Top bar -->
      <template #top>
        <div class="host-image p-1 pl-4 h-[72px] flex justify-center">
          <img
            class="h-full object-contain"
            :src="donorStore.hostLogo"
            :alt="donorStore.hostName"
            data-test="host-logo"
          />
        </div>
        <div class="mr-6">
          <Button
            class="hidden lg:block !text-sm"
            variant="link"
            as="a"
            :href="donorStore.hostUrl"
            data-test="return-button"
          >
            Return to Gives Day Home
          </Button>
        </div>
      </template>

      <!-- Bottom bar -->
      <template #bottom>
        <div class="flex flex-row items-center">
          <Transition name="fade">
            <Button
              v-show="step > 1 && step < 4"
              severity="secondary"
              :disabled="loading"
              label="Back"
              @click="back()"
              class="px-12"
              data-test="back-button"
            />
          </Transition>
        </div>
        <div class="flex flex-row items-center">
          <Transition name="fade">
            <Button
              v-show="existingAccount !== null"
              :disabled="!activeFormValid || loading"
              :loading="loading"
              :label="nextLabel"
              @click="next()"
              class="px-12"
              data-test="next-button"
            />
          </Transition>
        </div>
      </template>

      <!-- Content container -->
      <template #main>
        <div v-show="step < 4">
          <div class="flex justify-between mb-6 flex-col sm:flex-row sm:items-center">
            <h1 class="text-5xl font-bold text-primary">Company Giving</h1>
            <p
              class="text-base mt-2 sm:mt-0"
              v-show="
                !companyAdminStore.existingPendingApproval &&
                !companyAdminStore.existingPendingProfile &&
                !companyAdminStore.existingActiveProfile
              "
            >
              Step {{ step }}/3
            </p>
          </div>

          <!-- Step 1 -->
          <div
            v-show="step == 1"
            data-test="step-1"
          >
            <div v-show="existingAccount == 2">
              <h2 class="text-2xl mb-6">Donor Account</h2>
              <p class="text-base max-w-xs sm:max-w-[430px] mb-6">
                A donor account is required to set up a Company Giving profile. Review your information below and click
                "Next" to continue.
              </p>
              <AuthStatus />
            </div>
            <div v-show="existingAccount != 2">
              <h2 class="text-2xl mb-6">Donor Account Login</h2>
              <p class="text-base max-w-xs sm:max-w-[430px] mb-6">
                A donor account is required to set up a Company Giving profile. If you have a donor account already,
                please enter your login info. If you do not have a donor account, creating one is easy!
              </p>
              <div class="flex flex-col flex-wrap gap-4 mb-6 ml-2">
                <div class="flex items-center gap-2">
                  <RadioButton
                    v-model="existingAccount"
                    :value="1"
                    inputId="existingAccount-1"
                  />
                  <label for="existingAccount-1">I have a donor account</label>
                </div>
                <div class="flex items-center gap-2">
                  <RadioButton
                    v-model="existingAccount"
                    :value="0"
                    inputId="existingAccount-0"
                    data-test="create-account"
                  />
                  <label for="existingAccount-0">I need to create a donor account</label>
                </div>
              </div>
              <div class="min-h-[350px]">
                <TransitionGroup name="fade">
                  <LoginForm
                    :key="1"
                    v-show="existingAccount === 1"
                    class="max-w-xs sm:max-w-[430px]"
                    @submit="next"
                  />
                  <SignupForm
                    :key="0"
                    v-show="existingAccount === 0"
                    class="max-w-xs sm:max-w-[430px]"
                    @submit="next"
                  />
                </TransitionGroup>
              </div>
            </div>
          </div>

          <!-- Step 2 -->
          <div
            v-show="step == 2"
            data-test="step-2"
          >
            <div v-if="companyAdminStore.existingPendingApproval">
              <h2 class="text-2xl mb-6">Company Already Submitted</h2>
              <p class="text-base max-w-xs sm:max-w-[430px] mb-6">
                A company profile has already been submitted for this donor account and is under review. We will notify
                you when your company profile has been approved.
              </p>
            </div>
            <div v-else-if="companyAdminStore.existingPendingProfile">
              <h2 class="text-2xl mb-6">Company Awaiting Profile</h2>
              <p class="text-base max-w-xs sm:max-w-[430px] mb-6">
                Your company profile has been approved and is awaiting profile completion. Please continue to complete
                your company profile.
              </p>
            </div>
            <div v-else-if="companyAdminStore.existingActiveProfile">
              <h2 class="text-2xl mb-6">Company Profile Active</h2>
              <p class="text-base max-w-xs sm:max-w-[430px] mb-6">
                Your company profile has been approved and is active. You can update your company profile at any time.
              </p>
            </div>
            <div v-else>
              <h2 class="text-2xl mb-6">Company Information</h2>
              <p class="text-base max-w-xs sm:max-w-[430px] mb-6">Please provide the required company information.</p>
              <CompanyInformationForm class="max-w-xs sm:max-w-[430px]" />
            </div>
          </div>

          <!-- Step 3-->
          <div
            v-show="step == 3"
            data-test="step-3"
          >
            <h2 class="text-2xl mb-6">Review your information</h2>
            <div class="max-w-xs sm:max-w-[430px]">
              <p class="text-base mb-6">
                Please take a moment review your information before submitting your registration.
              </p>
              <div class="flex flex-col gap-6">
                <h2 class="text-2xl flex items-center">
                  Account Information
                  <Button
                    class="inline-block scale-75"
                    variant="text"
                    @click="back(2)"
                    ><i class="fa fa-pen"></i
                  ></Button>
                </h2>
                <div class="flex flex-col gap-1">
                  <label class="text-xs">Donor Account Holder</label>
                  <p data-test="donor-name">{{ donorStore.donorInfo.firstName }} {{ donorStore.donorInfo.lastName }}</p>
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-xs">Email Address</label>
                  <p data-test="donor-email">{{ donorStore.donorInfo.email }}</p>
                </div>
                <h2 class="text-2xl flex items-center">
                  Company Information
                  <Button
                    class="inline-block scale-75"
                    variant="text"
                    @click="back(1)"
                    ><i class="fa fa-pen"></i
                  ></Button>
                </h2>
                <div class="flex flex-col gap-1">
                  <label class="text-xs">Company Name</label>
                  <p data-test="company-name">{{ companyAdminStore.basicInfoForm?.states.name.value }}</p>
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-xs">Company Website</label>
                  <p data-test="company-website">{{ companyAdminStore.basicInfoForm?.states.url.value }}</p>
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-xs">Custom Url</label>
                  <p data-test="custom-url">{{ companyAdminStore.basicInfoForm?.states.customUrl.value }}</p>
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-xs">About Us</label>
                  <p data-test="company-aboutUs">{{ companyAdminStore.basicInfoForm?.states.aboutUs.value }}</p>
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-xs">Goal</label>
                  <p data-test="company-goal">{{ companyAdminStore.basicInfoForm?.states.companyGoal.value }}</p>
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-xs">Participation Text</label>
                  <p data-test="company-participation">{{ companyAdminStore.basicInfoForm?.states.participationText.value }}</p>
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-xs">Company Logo</label>
                  <img
                    v-if="companyAdminStore.basicInfoForm?.states.logoImage.value"
                    class="w-48 object-contain block rounded-md"
                    :src="companyAdminStore.basicInfoForm?.states.logoImage.value"
                    :alt="companyAdminStore.basicInfoForm?.states.name.value"
                    data-test="company-logo"
                  />
                  <p v-else>No logo uploaded</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-show="step == 4"
          class="flex flex-col gap-6 max-w-xs sm:max-w-[430px]"
          data-test="step-4"
        >
          <h1 class="text-5xl font-bold text-primary">Thank you!</h1>
          <h2 class="text-2xl">We&apos;ve received your company giving registration</h2>
          <p>
            Thank you for registering for company giving at {{ donorStore.hostName }}! Your application is now being
            reviewed by our gives day hosts. Once approved you will be able to access your company giving dashboard from
            your donor portal.
          </p>
        </div>
      </template>
    </StepsTakeover>
  </TransitionGroup>
</template>

<script setup>
import { ref, onBeforeMount, computed, watchEffect } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/AuthStore.js';
import { useCompanyAdminStore } from '@/stores/CompanyAdminStore.js';
import { useDonorStore } from '@/stores/DonorStore.js';
import { useToastStore } from '@/stores/ToastStore.js';

import AuthStatus from '@/components/auth/AuthStatus.vue';
import CompanyInformationForm from '@/components/companyGiving/CompanyInformationForm.vue';
import LoginForm from '@/components/auth/LoginForm.vue';
import SignupForm from '@/components/auth/SignupForm.vue';
import StepsTakeover from '@/components/StepsTakeover.vue';
import NotFound from '@/pages/NotFound.vue';

const authStore = useAuthStore();
const companyAdminStore = useCompanyAdminStore();
const donorStore = useDonorStore();
const router = useRouter();
const hostNotFound = ref(null);
const step = ref(1);
const existingAccount = ref(null);
const initialized = ref(false);
const loading = ref(false);
const slideOut = ref(false);
const slideIn = ref(false);

const activeFormValid = computed(() => {
  if (step.value === 1) {
    if (existingAccount.value === 2 && authStore.authenticated) {
      return true;
    }

    if (existingAccount.value === 1) {
      return authStore.loginForm.valid;
    }
    return authStore.signupForm?.valid;
  } else if (step.value === 2) {
    if (companyAdminStore.existingActiveProfile || companyAdminStore.existingPendingProfile) {
      return true;
    }
    return companyAdminStore.basicInfoForm?.valid;
  } else {
    // Steps 3 and 4
    return true;
  }
});

const nextLabel = computed(() => {
  if (step.value == 3) {
    return 'Submit';
  }
  if (step.value == 4) {
    return 'Go to Donor Portal';
  }
  return ['Sign Up', 'Log In', 'Continue'][existingAccount.value];
});

onBeforeMount(async () => {
  try {
    await donorStore.fetchHostFromPortalDomain();
    await authStore.checkLocalStorage();

    if (authStore.authenticated) {
      existingAccount.value = 2;
    }
    initialized.value = true;
  } catch (error) {
    console.error(error);
    hostNotFound.value = true;
  }
});

watchEffect(() => {
  // Logout from AuthStatus
  if (existingAccount.value == 2 && !authStore.authenticated) {
    existingAccount.value = null;
  } else if (!loading.value && authStore.authenticated && (existingAccount.value == 0 || existingAccount.value == 1)) {
    // Show existing account status if user is logged in, but not while loading (during login/signup)
    existingAccount.value = 2;
  }
});

const back = (by = 1) => {
  if (loading.value) return;

  if (step.value > 1) {
    // Animated transition in StepsTakeover
    requestAnimationFrame(() => {
      slideOut.value = false;
      slideIn.value = true;
      loading.value = true;
      setTimeout(() => {
        step.value -= parseInt(by);
        slideIn.value = false;
        loading.value = false;
      }, 500);
    });
  }
};

const next = async () => {
  if (loading.value) return;
  loading.value = true;

  try {
    switch (step.value) {
      // Sign up or log in
      case 1:
        if (existingAccount.value == 0) {
          await authStore.donorSignup();
        } else if (existingAccount.value == 1) {
          await authStore.donorLogin();
        }
        break;

      // Company information
      case 2:
        if (companyAdminStore.existingPendingProfile || companyAdminStore.existingActiveProfile) {
          router.push('/company/profile');
        }
        break;

      // Submit
      case 3:
        await companyAdminStore.submitBasicInfo();
        break;

      // Complete: return to donor portal
      case 4:
        router.push('/');
        return;
    }
  } catch (e) {
    // Report error as a Toast notification
    useToastStore().add({
      severity: 'error',
      summary: 'Error',
      detail: e.message,
      life: 3000,
    });
    loading.value = false;
    return;
  }

  // Animated transition in StepsTakeover
  requestAnimationFrame(() => {
    slideOut.value = true;
    slideIn.value = false;
    setTimeout(() => {
      step.value++;
      loading.value = false;
      slideOut.value = false;
    }, 500);
  });
};
</script>
